import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {createSelector} from "reselect";
import {capitalize} from "lodash";
import moment from "moment";
import axios from "axios";
import {useTranslation} from "react-i18next";
import logo from "../assets/logo-web.png";

// Libs
import {blueColor, lightBlueColor, boxShadow, greenColor, lightGreenColor, primaryColor, textColor} from "../common/libs/theme-lib";
import {formatNumber} from "../common/libs/numbers-lib";

// Hooks
import {getTokenFromLocalStorage, useAuth} from "../auth/auth";
import {useUserClientsData} from "../user-clients-data/user-clients-data";

// Components
import ClientSelector from "../common/components/client-selector";
import Chart from "../common/components/chart";
import useMediaQuery from "../common/hooks/use-media-query";

// Actions
import {readInvoicesStats} from "./invoices-stats-actions"
import {readDeliveryNotesStats} from "./delivery-notes-stats-actions";

// Selectors
import {invoicesStatsSelector, invoicesStatsIsFetchingSelector, invoicesStatsErrorSelector} from "./invoices-stats-selectors";
import {deliveryNotesStatsSelector, deliveryNotesStatsIsFetchingSelector, deliveryNotesStatsErrorSelector} from "./delivery-notes-stats-selector";

// Component Selector
const homeStatsPageSelector = createSelector(
    invoicesStatsSelector,
    invoicesStatsIsFetchingSelector,
    invoicesStatsErrorSelector,
    deliveryNotesStatsSelector,
    deliveryNotesStatsIsFetchingSelector,
    deliveryNotesStatsErrorSelector,
    (invoicesStats, invoicesStatsIsFetching, invoicesStatsError, deliveryNotesStats, deliveryNotesStatsIsFetching, deliveryNotesStatsError) => {
        return {
            invoicesStats: invoicesStats?.toJS() || [],
            invoicesStatsIsFetching,
            invoicesStatsError,
            deliveryNotesStats: deliveryNotesStats?.toJS() || [],
            deliveryNotesStatsIsFetching,
            deliveryNotesStatsError
        }
    }
);

// Redux dispatch hook
const useReduxDispatch = () => {
    const dispatch = useDispatch();
    return {
        readInvoicesStats: (clientId) => dispatch(readInvoicesStats(clientId)),
        readDeliveryNotesStats: (clientId) => dispatch(readDeliveryNotesStats(clientId))
    }
};

const HomePage = () => {

    // Redux
    const {
        invoicesStats, invoicesStatsIsFetching, invoicesStatsError,
        deliveryNotesStats, deliveryNotesStatsIsFetching, deliveryNotesStatsError
    } = useSelector(state => homeStatsPageSelector(state));
    const {readInvoicesStats, readDeliveryNotesStats} = useReduxDispatch();

    const {selectedClient, userType, features} = useAuth();
    const {userClientsData} = useUserClientsData();
    const {t} = useTranslation("home");
    const mSize = useMediaQuery("(min-width: 1000px)");

    useEffect(() => {
        if (!!selectedClient) {
            if (userType === "admin" || userType === "client" && features.indexOf("show-stats") >= 0) {
                if (invoicesStats.length === 0) readInvoicesStats(selectedClient);
                if (deliveryNotesStats.length === 0) readDeliveryNotesStats(selectedClient);
            }
        }
    }, [selectedClient]);

    const selectedClientData = userClientsData.find(client => client._id === selectedClient);

    return (
        <>
            <div style={{
                display: "flex",
                alignItems: "flex-end",
                paddingBottom: "15px"
            }}>
                <div style={{flex: 1}}>
                    <div style={{
                        color: primaryColor,
                        fontSize: 28,
                        fontWeight: "bold"
                    }}>{t("title", {client: selectedClientData && selectedClientData.name})}</div>
                    {userType === "admin" && (
                        <div style={{fontWeight: 100, fontSize: 18}}>
                            {t("description")}
                        </div>
                    )}
                    {userType === "client" && features.indexOf("show-stats") >= 0 && (
                        <div style={{fontWeight: 100, fontSize: 18}}>
                            {t("description")}
                        </div>
                    )}
                </div>
                {userType === "admin" && <ClientSelector/>}
            </div>
            <div style={{
                backgroundColor: "white",
                boxShadow: boxShadow,
                padding: 15,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                color: textColor
            }}>
                {userType === "admin" || (userType === "client" && features.indexOf("show-stats") >= 0) ? (
                    <div
                        style={{
                            display: "flex",
                            flex: 1,
                            flexDirection: "column",
                            paddingBottom: 30
                        }}
                    >
                        <div style={{
                            fontSize: 20,
                            fontWeight: "bold"
                        }}>
                            {t("invoicesChart.title")}
                        </div>
                        <div style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: mSize ? "row" : "column"
                        }}>
                            <div style={{
                                minWidth: 250,
                                display: "flex",
                                flexWrap: "wrap",
                                flexDirection: mSize ? "column" : "row"
                            }}>
                                <div style={kpiStyles.wrapper}>
                                    <div style={kpiStyles.title}>{t("lastYear")}</div>
                                    <div>
                                        <span style={kpiStyles.count.value}>{getTotalFromData(invoicesStats, "past-count")}</span>
                                        <span style={kpiStyles.count.text}>{t("invoices")}</span>
                                    </div>
                                    <div style={kpiStyles.count.value}>
                                        <span style={{marginRight: 5}}>{formatNumber(getTotalFromData(invoicesStats, "past-total"))}</span>
                                        <span>{"€"}</span>
                                    </div>
                                </div>
                                <div style={kpiStyles.wrapper}>
                                    <div style={kpiStyles.title}>{t("currentYear")}</div>
                                    <div>
                                        <span style={kpiStyles.total.value}>{getTotalFromData(invoicesStats, "actual-count")}</span>
                                        <span style={kpiStyles.total.text}>{t("invoices")}</span>
                                    </div>
                                    <div style={kpiStyles.total.value}>
                                        <span style={{marginRight: 5}}>{formatNumber(getTotalFromData(invoicesStats, "actual-total"))}</span>
                                        <span>{"€"}</span>
                                    </div>
                                </div>
                            </div>
                            <Chart
                                id={"graph1"}
                                config={{
                                    grid: {
                                        top: 20
                                    },
                                    legend: {
                                        top: "bottom"
                                    },
                                    tooltip: {
                                        trigger: "axis",
                                        axisPointer: {
                                            type: "shadow"
                                        },
                                        formatter: params => {
                                            return `
                                    <div>
                                        <div>
                                            ${capitalize(moment(params[0].name, "M").format("MMMM"))}
                                        </div>
                                        ${params.map((param, index) => {
                                                return `
                                                <div key={${index}} style="display:flex;">
                                                    <div style="flex:1; padding-right:10px;">
                                                        ${param.marker} ${param.seriesName}
                                                    </div>
                                                    <div style="font-weight:bold;">
                                                        ${param.seriesType === "bar" ? formatNumber(param.value[param.seriesId]) : param.value[param.seriesId] || 0}
                                                    </div>
                                                </div>
                                            `;
                                            }).join("")}
                                    </div>
                                `;
                                        }
                                    },
                                    xAxis: {
                                        type: "category",
                                        axisLabel: {
                                            formatter: value => capitalize(moment(value, "M").format("MMM")).substr(0, 3)
                                        }
                                    },
                                    yAxis: [{
                                        type: "value",
                                        axisLine: {
                                            show:true
                                        },
                                        splitLine: {
                                            lineStyle: {
                                                type: "dashed"
                                            }
                                        }
                                    }, {
                                        type: "value",
                                        axisLine: {
                                            show:true
                                        },
                                        splitLine: {
                                            show:false
                                        },
                                        axisLabel: {
                                            formatter: value => formatNumber(value)
                                        },
                                    }],
                                    series: [{
                                        type: 'line',
                                        id: "past-count",
                                        name: t("invoicesChart.series.lastInvoices"),
                                        itemStyle: {
                                            color: greenColor
                                        }
                                    }, {
                                        type: 'line',
                                        id: "actual-count",
                                        name: t("invoicesChart.series.currentInvoices"),
                                        itemStyle: {
                                            color: blueColor
                                        }
                                    }, {
                                        type: 'bar',
                                        id: "past-total",
                                        name: t("invoicesChart.series.lastTotal"),
                                        itemStyle: {
                                            color: lightGreenColor
                                        },
                                        yAxisIndex: 1
                                    }, {
                                        type: 'bar',
                                        id: "actual-total",
                                        name: t("invoicesChart.series.currentTotal"),
                                        itemStyle: {
                                            color: lightBlueColor
                                        },
                                        yAxisIndex: 1
                                    }]
                                }}
                                data={{
                                    dimensions: ["month", "past-count", "actual-count", "past-total", "actual-total"],
                                    source: invoicesStats
                                }}
                            />
                        </div>
                    </div>
                ) : null}
                {userType === "admin" || (userType === "client" && features.indexOf("show-stats") >= 0) ? (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            flex: 1,
                            paddingBottom: 30
                        }}
                    >
                        <div style={{
                            fontSize: 20,
                            fontWeight: "bold"
                        }}>
                            {t("deliveryNotesChart.title")}
                        </div>
                        <div style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: mSize ? "row" : "column"
                        }}>
                            <div style={{
                                minWidth: 250,
                                display: "flex",
                                flexWrap: "wrap",
                                flexDirection: mSize ? "column" : "row"
                            }}>
                                <div style={kpiStyles.wrapper}>
                                    <div style={kpiStyles.title}>{t("lastYear")}</div>
                                    <div>
                                        <span style={kpiStyles.count.value}>{getTotalFromData(deliveryNotesStats, "past-count")}</span>
                                        <span style={kpiStyles.count.text}>{t("deliveryNotes")}</span>
                                    </div>
                                </div>
                                <div style={kpiStyles.wrapper}>
                                    <div style={kpiStyles.title}>{t("currentYear")}</div>
                                    <div>
                                        <span style={kpiStyles.total.value}>{getTotalFromData(deliveryNotesStats, "actual-count")}</span>
                                        <span style={kpiStyles.total.text}>{t("deliveryNotes")}</span>
                                    </div>
                                </div>
                            </div>
                            <Chart
                                id={"graph2"}
                                config={{
                                    grid: {
                                        top: 20
                                    },
                                    legend: {
                                        top: "bottom"
                                    },
                                    tooltip: {
                                        trigger: "axis",
                                        axisPointer: {
                                            type: "shadow"
                                        },
                                        formatter: params => {
                                            return `
                                <div>
                                    <div>
                                        ${capitalize(moment(params[0].name, "M").format("MMMM"))}
                                    </div>
                                    ${params.map((param, index) => {
                                                return `
                                            <div key={${index}} style="display:flex;">
                                                <div style="flex:1; padding-right:10px;">
                                                    ${param.marker} ${param.seriesName}
                                                </div>
                                                <div style="font-weight:bold;">
                                                    ${param.value[param.seriesId] || 0}
                                                </div>
                                            </div>
                                        `;
                                            }).join("")}
                                </div>
                            `;
                                        }
                                    },
                                    xAxis: {
                                        type: "category",
                                        axisLabel: {
                                            formatter: value => capitalize(moment(value, "M").format("MMM")).substr(0, 3)
                                        }
                                    },
                                    yAxis: [{
                                        type: "value",
                                        axisLine: {
                                            show:true
                                        },
                                        splitLine: {
                                            lineStyle: {
                                                type: "dashed"
                                            }
                                        }
                                    }],
                                    series: [{
                                        type: 'line',
                                        id: "past-count",
                                        name: t("deliveryNotesChart.series.lastDeliveryNotes"),
                                        itemStyle: {
                                            color: greenColor
                                        }
                                    }, {
                                        type: 'line',
                                        id: "actual-count",
                                        name: t("deliveryNotesChart.series.currentDeliveryNotes"),
                                        itemStyle: {
                                            color: blueColor
                                        }
                                    }]
                                }}
                                data={{
                                    dimensions: ["month", "past-count", "actual-count"],
                                    source: deliveryNotesStats
                                }}
                            />
                        </div>
                    </div>
                ): null}
                {userType === "client" && features.indexOf("show-stats") === -1 ? (
                    <div style={{margin: "auto"}}>
                        <img src={logo} style={{display: "block", margin: "30px auto"}} />
                        <h3 style={{fontSize: 24}}>{t("noFeaturesMessage")}</h3>
                    </div>
                ) : null}
            </div>
        </>
    );
};

export default HomePage;

const getTotalFromData = (data, field) => {
    return data.reduce((acc, itemData) => acc + (itemData[field] || 0), 0);
};

const kpiStyles = {
    wrapper: {
        flex:1,
        padding: 10
    },
    title: {
        fontSize: 18,
        fontWeight: "bold"
    },
    count: {
        value: {
            fontSize: 24,
            fontWeight: "bold",
            color: greenColor,
            marginRight: 5
        },
        text: {
            fontSize: 18
        }
    },
    total: {
        value: {
            fontSize: 24,
            fontWeight: "bold",
            color: blueColor,
            marginRight: 5
        },
        text: {
            fontSize: 18
        }
    }
};